import { map } from 'rxjs/operators';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';

import { LocationService } from '../../locations/location.service';
import { NamingService } from '../../services/naming.service';
import { SampleService } from '../sample.service';
import { SampleLogicService } from '../sample-logic.service';
import { SampleVocabService } from '../sample-vocab.service';
import { ViewSampleAuditReportComponentService } from '../audit';

import {
    BaseDetail,
    BaseDetailService,
    FacetView,
    IFacet,
    PageState,
} from '../../common/facet';

import { IValidatable, SaveChangesService, UnsavedChanges } from '../../services/save-changes.service';
import { DotmaticsService } from '../../dotmatics/dotmatics.service';
import { SettingService } from '../../settings/setting.service';
import { FeatureFlagService } from '../../services/feature-flags.service';
import { dateControlValidator } from '@common/util/date-control.validator';
import { CharacteristicInputComponent } from 'src/app/characteristics/characteristic-input/characteristic-input.component';
import { LocationHistoryComponent } from 'src/app/locations/location-history.component';
import { OrderSearch } from '@common/types/models/order-search.interface';
import {
    Entity,
    Line,
    MaterialSourceMaterial,
    Sample,
    cv_ContainerType,
    cv_MaterialOrigin,
    cv_PreservationMethod,
    cv_SampleAnalysisMethod,
    cv_SampleProcessingMethod,
    cv_SampleStatus,
    cv_SampleSubtype,
    cv_SampleType,
    cv_TimeUnit,
    cv_Unit
} from '@common/types';

@Component({
    selector: 'sample-detail',
    templateUrl: './sample-detail.component.html'
})
export class SampleDetailComponent extends BaseDetail implements OnChanges, OnInit, OnDestroy, IValidatable {
    @ViewChildren('characteristicInput') characteristicInputs: CharacteristicInputComponent[];
    @ViewChildren('locationHistory') locationHistories: LocationHistoryComponent[];
    @ViewChildren('dateControl') dateControls: NgModel[];
    @Input() facet: IFacet;
    @Input() sample: Entity<Sample>;
    @Input() pageState: PageState;
    @Input() requiredFields: string[] = [];
    @Input() activeFields: string[] = [];

    @Output() exit: EventEmitter<void> = new EventEmitter<void>();
    @Output() next: EventEmitter<void> = new EventEmitter<void>();
    @Output() previous: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild("sampleForm") sampleForm: NgForm;

    facetView: FacetView = FacetView.DETAIL_VIEW;

    // CVs
    containerTypes: cv_ContainerType[];
    materialOrigins: cv_MaterialOrigin[];
    preservationMethods: cv_PreservationMethod[];
    sampleStatuses: cv_SampleStatus[];
    sampleTypes: cv_SampleType[];
    timeUnits: cv_TimeUnit[];
    units: cv_Unit[];
    sampleSubtypes: cv_SampleSubtype[];
    sampleProcessingMethods: cv_SampleProcessingMethod[];
    sampleAnalysisMethods: cv_SampleAnalysisMethod[];
    orders: OrderSearch[];

    // State
    sampleNamingActive = false;

    sampleStatusDefaultKey: number | null;
    unitDefaultKey: number | null;
    sampleTypeDefaultKey: number | null;
    preservationMethodDefaultKey: number | null;
    containerTypeDefaultKey: number | null;
    materialOriginDefaultKey: number | null;
    sampleSubtypeDefaultKey: number | null;
    sampleProcessingMethodDefaultKey: number | null;
    sampleAnalysisMethodDefaultKey: number | null;

    readonly COMPONENT_LOG_TAG = 'sample-detail';

    // Dotmatics workgroup flag
    isDotmatics: boolean;

    isGLP = false;

    constructor(
        private baseDetailService: BaseDetailService,
        private locationService: LocationService,
        private namingService: NamingService,
        private sampleService: SampleService,
        private sampleLogicService: SampleLogicService,
        private sampleVocabService: SampleVocabService,
        private viewSampleAuditReportComponentService: ViewSampleAuditReportComponentService,
        private saveChangesService: SaveChangesService,
        private dotmaticsService: DotmaticsService,
        private settingService: SettingService,
        private featureFlagService: FeatureFlagService,
    ) {
        super(baseDetailService);

    }

    // lifecycle
    ngOnInit(): void {
        this.saveChangesService.registerValidator(this);
        this.initialize();
    }

    ngOnChanges(changes: any): void {
        if (changes.sample) {
            if (this.sample && !changes.sample.firstChange) {
                if (this.sampleForm) {
                    this.sampleForm.form.markAsPristine();
                }
                this.initialize();
            }
        }
    }

    ngOnDestroy(): void {
        this.saveChangesService.unregisterValidator(this);
    }

    initialize(): void {
        this.setLoading(true);
        this.setIsDotmatics();
        this.initIsGLP();

        this.getCVs().then(() => {
            this.setDefaultCvValues();
        }).then(() => {
            this.getOrders();
            return this.isNamingActive();
        }).then(() => {
            return this.getDetails();
        }).then(() => {
            this.setLoading(false);
        }).catch((error) => {
            this.setLoading(false);
            throw error;
        });
    }

    private getCVs(): Promise<any> {
        const p1 = this.sampleVocabService.containerTypes$.pipe(map((data) => {
            this.containerTypes = data;
        })).toPromise();

        const p2 = this.sampleVocabService.materialOrigins$.pipe(map((data) => {
            this.materialOrigins = data;
        })).toPromise();

        const p3 = this.sampleVocabService.preservationMethods$.pipe(map((data) => {
            this.preservationMethods = data;
        })).toPromise();

        const p4 = this.sampleVocabService.sampleStatuses$.pipe(map((data) => {
            this.sampleStatuses = data;
        })).toPromise();

        const p5 = this.sampleVocabService.sampleTypes$.pipe(map((data) => {
            this.sampleTypes = data;
        })).toPromise();

        const p6 = this.sampleVocabService.units$.pipe(map((data) => {
            this.units = data;
        })).toPromise();

        const p7 = this.sampleVocabService.timeUnits$.pipe(map((data) => {
            this.timeUnits = data;
        })).toPromise();

        const p8 = this.sampleVocabService.sampleSubtypes$.pipe(map((data) => {
            this.sampleSubtypes = data;
        })).toPromise();

        const p9 = this.sampleVocabService.sampleProcessingMethods$.pipe(map((data) => {
            this.sampleProcessingMethods = data;
        })).toPromise();

        const p10 = this.sampleVocabService.sampleAnalysisMethods$.pipe(map((data) => {
            this.sampleAnalysisMethods = data;
        })).toPromise();

        return Promise.all([p1, p2, p3, p4, p5, p6, p7, p8, p9, p10]);
    }

    private setDefaultCvValues() {
        this.sampleStatusDefaultKey = this.sampleStatuses.find((item) => item.IsDefault)?.C_SampleStatus_key;
        this.unitDefaultKey = this.units.find((item) => item.IsDefault)?.C_Unit_key;
        this.sampleTypeDefaultKey = this.sampleTypes.find((item) => item.IsDefault)?.C_SampleType_key;
        this.preservationMethodDefaultKey = this.preservationMethods.find((item) => item.IsDefault)?.C_PreservationMethod_key;
        this.containerTypeDefaultKey = this.containerTypes.find((item) => item.IsDefaultSample)?.C_ContainerType_key;
        this.materialOriginDefaultKey = this.materialOrigins.find((item) => item.IsDefault)?.C_MaterialOrigin_key;
        this.sampleSubtypeDefaultKey = this.sampleSubtypes.find((item) => item.IsDefault)?.C_SampleSubtype_key;
        this.sampleProcessingMethodDefaultKey = this.sampleProcessingMethods.find((item) => item.IsDefault)?.C_SampleProcessingMethod_key;
        this.sampleAnalysisMethodDefaultKey = this.sampleAnalysisMethods.find((item) => item.IsDefault)?.C_SampleAnalysisMethod_key;
    }

    private isNamingActive(): Promise<void> {
        return this.namingService.isSampleNamingActive()
            .then((active: boolean) => {
                this.sampleNamingActive = active;
            });
    }

    private getDetails(): Promise<Sample> {
        if (this.sample && this.sample.C_Material_key > 0) {
            const expands: string[] = [];

            return this.sampleService.getSample(this.sample.C_Material_key, expands)
                .then(() => {
                    return this.attachCharacteristicEnumerations();
                });
        } else {
            return Promise.resolve(this.sample);
        }
    }

    private attachCharacteristicEnumerations(): Promise<any> {
        if (this.sample.SampleCharacteristicInstance?.length > 0) {
            return this.sampleLogicService.attachEnumerations(this.sample.SampleCharacteristicInstance);
        } else {
            return Promise.resolve();
        }
    }

    onTypeChanged(): void {
        const characteristicsCount = this.sample.SampleCharacteristicInstance.length
        let alreadyRemoved = 0

        while (alreadyRemoved < characteristicsCount) {
            this.sampleService.deleteSampleCharacteristic(this.sample.SampleCharacteristicInstance[characteristicsCount - 1 - alreadyRemoved])
            alreadyRemoved++
        }

        this.sampleLogicService.createCharacteristics(this.sample)
    }

    async getOrders(): Promise<void>  {
        this.sampleService.getSampleOrders().then((data) => {
            this.orders = data;
        });
    }

    // Model change events
    onLineSelection(lineKey: number): Promise<Line | void> {
        return this.sampleLogicService.onLineSelection(lineKey, this.sample);
    }

    onSourceSelection(sourceMaterial: MaterialSourceMaterial): Promise<void> {
        return this.sampleLogicService.onSourceSelection(this.sample, sourceMaterial);
    }

    // Location
    createMaterialLocation(): void {
        this.locationService.getDefaultLocation().then((defaultLocation) => {
            const initialValues = {
                C_LocationPosition_key: defaultLocation.C_LocationPosition_key,
                C_Material_key: this.sample.C_Material_key
            };

            return this.locationService.createMaterialLocation(initialValues);
        });
    }

    removeLocation(materialLocation: any): void {
        this.locationService.deleteMaterialLocation(materialLocation);
    }


    // Source
    getSourceAgeInWeeksAtHarvest(): string {
        return this.sampleLogicService.getSourceAgeInWeeksAtHarvest(this.sample);
    }


    // Audit
    viewAuditReport(): void {
        this.viewSampleAuditReportComponentService.openComponent(
            this.sample.C_Material_key
        );
    }

    onCancel(): void {
        this.sampleService.cancelSample(this.sample);
    }

    async validate(): Promise<string> {
        let message = '';
        message = await this.settingService.validateRequiredFields(this.requiredFields, this.sample, 'sample');

        return message;
    }

    async onSaveSample(): Promise<void> {
        const errMsg = await this.validateSample();

        if (errMsg) {
            this.loggingService.logError(errMsg, 'Validation Error', this.COMPONENT_LOG_TAG, true);
            return;
        }

        await this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG);

        if (this.isDotmatics && this.sample.Material.MaterialExternalSync.length > 0) {
            this.dotmaticsService.updateDotmaticsSample(this.sample.C_Material_key);
        }
    }

    /**
     * Sets isDotmatics flag
     */
    private setIsDotmatics(): void {
        this.isDotmatics = this.dotmaticsService.setIsDotmatics();
    }

    private async validateSample(): Promise<string> {
        let errorMessage = this.characteristicInputs.map(input => input.validate()).find(msg => msg)
            || this.locationHistories.map(input => input.validate()).find(msg => msg)
            || dateControlValidator(this.dateControls)
            || await this.settingService.validateRequiredFields(this.requiredFields, this.sample, 'sample');

        if (!errorMessage && !this.locationService.locationsValid(this.sample.Material.MaterialLocation)) {
            errorMessage = 'Locations dates are not valid.';
        }

        return errorMessage;
    }

    initIsGLP(): void {
        this.isGLP = this.featureFlagService.isFlagOn('IsGLP');
    }

    onSwitchView(buttonName: string): Promise<void> {
        if (this.saveChangesService.hasChanges) {
            // If there are unsaved changes, prompt the user to save or discard
            return this.viewUnsavedChangesModalService.openComponent().then((result: string) => {
                if (result === 'save') {
                    // Throw error & don't save if location dates are invalid
                    if (this.locationService.locationsValid(this.sample.Material.MaterialLocation)) {
                        return this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG).then(() => {
                            // Emits reload event when save a new record by clicking on next
                            if (buttonName === 'next') {
                                this.reload.emit();
                            }
                            this.emitViewChange(buttonName, UnsavedChanges.save);
                        });
                    } else {
                        this.loggingService.logError("Locations dates are not valid.", null, this.COMPONENT_LOG_TAG, true);
                    }
                } else {
                    if (this.dataContext.hasChanges()) {
                        this.onCancel();
                        this.loggingService.logFacetUndoSuccess(this.COMPONENT_LOG_TAG);
                    }
                    this.emitViewChange(buttonName, UnsavedChanges.discard);
                }
            });
        } else {
            this.emitViewChange(buttonName, UnsavedChanges.noChanges);
        }
    }

    private emitViewChange(buttonName: string, changes: UnsavedChanges) {
        switch (buttonName) {
            case 'previous':
                super.previousClicked(changes);
                this.previous.emit();
                break;
            case 'next':
                super.nextClicked(changes);
                this.next.emit();
                break;
            case 'exit':
                super.exitClicked(changes);
                this.exit.emit();
                break;
            default:
                break;
        }
    }


    // <select> formatters
    sampleTypeKeyFormatter = (value: cv_SampleType): number => {
        return value.C_SampleType_key;
    }
    sampleTypeFormatter = (value: cv_SampleType): string => {
        return value.SampleType;
    }
    sampleStatusKeyFormatter = (value: cv_SampleStatus): number => {
        return value.C_SampleStatus_key;
    }
    sampleStatusFormatter = (value: cv_SampleStatus): string => {
        return value.SampleStatus;
    }
    preservationMethodKeyFormatter = (value: cv_PreservationMethod): number => {
        return value.C_PreservationMethod_key;
    }
    preservationMethodFormatter = (value: cv_PreservationMethod): string => {
        return value.PreservationMethod;
    }
    materialOriginKeyFormatter = (value: cv_MaterialOrigin): number => {
        return value.C_MaterialOrigin_key;
    }
    materialOriginFormatter = (value: cv_MaterialOrigin): string => {
        return value.MaterialOrigin;
    }
    containerTypeKeyFormatter = (value: cv_ContainerType): number => {
        return value.C_ContainerType_key;
    }
    containerTypeFormatter = (value: cv_ContainerType): string => {
        return value.ContainerType;
    }
    timeUnitKeyFormatter = (value: cv_TimeUnit): number => {
        return value.C_TimeUnit_key;
    }
    timeUnitFormatter = (value: cv_TimeUnit): string => {
        return value.TimeUnit;
    }
    unitKeyFormatter = (value: cv_Unit): number => {
        return value.C_Unit_key;
    }
    unitFormatter = (value: cv_Unit): string => {
        return value.Unit;
    }
    sampleSubtypeKeyFormatter = (value: cv_SampleSubtype): number => {
        return value.C_SampleSubtype_key;
    }
    sampleSubtypeFormatter = (value: cv_SampleSubtype): string => {
        return value.SampleSubtype;
    }
    sampleProcessingMethodKeyFormatter = (value: cv_SampleProcessingMethod): number => {
        return value.C_SampleProcessingMethod_key;
    }
    sampleProcessingMethodFormatter = (value: cv_SampleProcessingMethod): string => {
        return value.SampleProcessingMethod;
    }
    sampleAnalysisMethodKeyFormatter = (value: cv_SampleAnalysisMethod): number => {
        return value.C_SampleAnalysisMethod_key;
    }
    sampleAnalysisMethodFormatter = (value: cv_SampleAnalysisMethod): string => {
        return value.SampleAnalysisMethod;
    }
}
